// LAYOUT-SPEC §0 — Teams (the SOW-first index, D109)
//
//   ONE QUESTION  Which teams does this program run, and which of them owe
//                 something now?
//   PRIMARY OBJECT  A collection of workstreams (Archetype B), plus one row for
//                 the contract-wide deliverables, "Contract-wide (<period>)".
//   PRIMARY VERB    Open a team. New team is the one create.
//   WORST-CASE VOLUME  ~25 teams (OMEGA has 23). One page, no paging.
//
// The Responsibilities view (?view=responsibilities) is the same teams read
// the other way: who owes, sees, copies or inherits each deliverable in one
// contract period (ResponsibilityMatrix).
//
// Each row's numbers are that team's own current window (D104): the in-force
// SOW's interval, else its exercise line's, plus open items carried over.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraEntityCell, SpecraFormField, SpecraInput, SpecraListPage, SpecraPageHeader, SpecraTabBar, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Layers } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { FileUploader } from '../../../../components/ProjectList/FileUploader';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { EmptyState } from '../../_shared/components/PageStates';
import { SOW_DOCUMENT_CATEGORY_HELP } from '../../_shared/constants/documentCategories';
import { SowIntakeStartError, useUploadSowDocument } from '../../Docs/useUploadSowDocument';
import { useContractCLINs } from '../../Programs/contractHooks';
import { useProgram } from '../../Programs/hooks';
import { useCmAccess } from '../access';
import { CmDrawer } from '../components/CmDrawer';
import { CmTable } from '../components/common';
import { ResponsibilityMatrix } from '../components/ResponsibilityMatrix';
import { TeamLinesPicker, teamLineNumbers } from '../components/TeamLines';
import { OccurrenceWindow, useCreateWorkstream, useOccurrences, useWorkstreams, useWorkstreamWindowCounts } from '../hooks';
import { todayISO } from '../lib/format';
import { useCmLookups } from '../lookups';
const COUNT_COLUMNS = [
    {
        state: 'late',
        header: 'Late'
    },
    {
        state: 'rejected',
        header: 'Rejected'
    },
    {
        state: 'awaiting_review',
        header: 'Awaiting review'
    },
    {
        state: 'due_soon',
        header: 'Due 14 days'
    }
];
function contractWideCounts(r) {
    if (!r) return undefined;
    const out = {};
    for (const o of r.occurrences){
        if (o.workstreamId || o.retired) continue;
        const s = o.status?.state ?? '';
        out[s] = (out[s] ?? 0) + 1;
    }
    return out;
}
export function useContractWideLabel(programId) {
    const lk = useCmLookups(programId);
    const today = todayISO();
    const current = lk.periods.find((p)=>p.start && p.start <= today && (!p.end || today <= p.end));
    return `Contract-wide${current ? ` (${current.name})` : ''}`;
}
export function TeamsPage() {
    const { programId } = useProgramContext();
    const navigate = useNavigate();
    const access = useCmAccess(programId);
    const teams = useWorkstreams(programId);
    const list = useMemo(()=>teams.data ?? [], [
        teams.data
    ]);
    // Every team's own current-window counts, one read (SAD-997).
    const windows = useWorkstreamWindowCounts(programId);
    const wide = useOccurrences(programId, {
        window: OccurrenceWindow.CONTRACT_CURRENT_PERIOD
    });
    const wideLabel = useContractWideLabel(programId);
    const [creating, setCreating] = useState(false);
    const [sp, setSp] = useSearchParams();
    const view = sp.get('view') === 'responsibilities' ? 'responsibilities' : 'teams';
    const setView = (next)=>setSp((prev)=>{
            const n = new URLSearchParams(prev);
            if (next === 'teams') n.delete('view');
            else n.set('view', next);
            return n;
        });
    const rows = useMemo(()=>{
        const teamRows = list.map((w)=>({
                id: w.id,
                name: w.name,
                contractWide: false,
                counts: windows.data?.get(w.id)?.stateCounts
            })).sort((a, b)=>a.name.localeCompare(b.name));
        const wideCounts = contractWideCounts(wide.data);
        const hasWide = wideCounts && Object.keys(wideCounts).length > 0;
        return hasWide || teamRows.length > 0 ? [
            {
                id: 'contract-wide',
                name: wideLabel,
                contractWide: true,
                counts: wideCounts
            },
            ...teamRows
        ] : [];
    }, [
        list,
        windows.data,
        wide.data,
        wideLabel
    ]);
    const columns = [
        {
            id: 'name',
            header: 'Team',
            cell: (r)=>/*#__PURE__*/ _jsx("div", {
                    "data-testid": r.contractWide ? Testids.cmTeams.contractWideRow : undefined,
                    children: /*#__PURE__*/ _jsx(SpecraEntityCell, {
                        title: r.name,
                        description: r.contractWide ? 'Deliverables no team owes' : undefined
                    })
                })
        },
        ...COUNT_COLUMNS.map((c)=>({
                id: c.state,
                header: c.header,
                align: 'right',
                cell: (r)=>{
                    const n = r.counts?.[c.state] ?? 0;
                    const alarm = n > 0 && (c.state === 'late' || c.state === 'rejected');
                    return /*#__PURE__*/ _jsx("span", {
                        className: n > 0 ? '' : 'text-muted-foreground',
                        "data-testid": Testids.cmTeams.rowStrip,
                        "data-state": c.state,
                        children: !r.counts ? '…' : alarm ? /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "danger",
                            children: n
                        }) : n
                    });
                }
            }))
    ];
    const empty = /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.cmTeams.empty,
        children: /*#__PURE__*/ _jsx(EmptyState, {
            icon: Layers,
            title: "No teams yet",
            description: "Teams come from exercise lines; add one by hand to start tracking what it owes.",
            action: access.canCreate ? /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>setCreating(true),
                children: "New team"
            }) : undefined
        })
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(SpecraListPage, {
                "data-testid": Testids.cmTeams.page,
                header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
                    titleSize: "home",
                    title: "Teams",
                    meta: list.length,
                    description: view === 'teams' ? 'Each team follows its SOW. Numbers are each team’s current window.' : 'Who owes each deliverable, and who only sees it, one contract period at a time.',
                    actions: access.canCreate ? /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>setCreating(true),
                        "data-testid": Testids.cmTeams.create,
                        children: "New team"
                    }) : undefined
                }),
                children: [
                    /*#__PURE__*/ _jsx(SpecraTabBar, {
                        label: "Teams views",
                        value: view,
                        onValueChange: setView,
                        "data-testid": Testids.cmTeams.views,
                        options: [
                            {
                                value: 'teams',
                                label: 'Teams'
                            },
                            {
                                value: 'responsibilities',
                                label: 'Responsibilities'
                            }
                        ]
                    }),
                    view === 'responsibilities' ? /*#__PURE__*/ _jsx(ResponsibilityMatrix, {
                        programId: programId
                    }) : /*#__PURE__*/ _jsx(CmTable, {
                        caption: "Teams",
                        rows: rows,
                        columns: columns,
                        rowKey: (r)=>r.id,
                        rowTestId: Testids.cmTeams.row,
                        isLoading: teams.isLoading,
                        error: teams.error,
                        onRetry: ()=>void teams.refetch(),
                        onRowClick: (r)=>navigate(`/delivery/programs/${programId}/workstreams/${r.id}`),
                        getRowLabel: (r)=>r.name,
                        empty: empty
                    })
                ]
            }),
            creating ? /*#__PURE__*/ _jsx(NewTeamDrawer, {
                programId: programId,
                onClose: ()=>setCreating(false),
                onCreated: (id)=>navigate(`/delivery/programs/${programId}/workstreams/${id}`)
            }) : null
        ]
    });
}
export function NewTeamDrawer({ programId, onClose, onCreated }) {
    const [name, setName] = useState('');
    const [base, setBase] = useState([]);
    const [everyPeriod, setEveryPeriod] = useState(true);
    // Optional SOW / Execution Plan staged with the new team (SAD-898): after
    // create, upload with this team's workstreamId (no team guessing).
    const [sowFiles, setSowFiles] = useState([]);
    const clins = useContractCLINs(programId);
    const { data: programData } = useProgram(programId);
    const zouProjectId = programData?.program?.zouProjectId ?? '';
    const create = useCreateWorkstream(programId, {
        success: 'Team created'
    });
    const uploadSow = useUploadSowDocument();
    const busy = create.isPending || uploadSow.isPending;
    const save = ()=>{
        create.mutate({
            name: name.trim(),
            lineNumbers: teamLineNumbers(base, everyPeriod, clins.data?.clins ?? [])
        }, {
            onSuccess: async (r)=>{
                const id = r.workstream?.id;
                if (!id) return;
                const file = sowFiles[0];
                if (file && zouProjectId) {
                    try {
                        await uploadSow.mutateAsync({
                            programId,
                            zouProjectId,
                            workstreamId: id,
                            file,
                            title: file.name.replace(/\.[^.]+$/, '') || name.trim()
                        });
                    } catch (err) {
                        // Intake failures already toast from useUploadSowDocument; a
                        // second "upload failed" would push the user to re-upload and
                        // record another document for the same file.
                        if (!(err instanceof SowIntakeStartError)) {
                            toast.error('Team created, but the SOW / Execution Plan upload failed — upload it from the team page.');
                        }
                    }
                } else if (file && !zouProjectId) {
                    toast.error('Team created, but this program has no linked contract project to upload a SOW against.');
                }
                onCreated(id);
            }
        });
    };
    return /*#__PURE__*/ _jsxs(CmDrawer, {
        title: "New team",
        description: "A team is one body of work: the contract lines it performs. Pick its base-period lines here; an optional task's line is linked by its exercise on the team page.",
        onClose: onClose,
        busy: busy,
        testId: Testids.cmTeams.createDrawer,
        footerProps: {
            confirmLabel: 'Create team',
            onConfirm: save,
            confirmDisabled: !name.trim() || busy,
            loading: busy,
            onCancel: onClose,
            confirmProps: {
                'data-testid': Testids.cmTeams.createSave
            }
        },
        children: [
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Name",
                required: true,
                hint: "As the SOW or the exercise line names it.",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    value: name,
                    onChange: (e)=>setName(e.target.value),
                    maxLength: 300,
                    "data-testid": Testids.cmTeams.createName
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Lines it owns",
                hint: "Optional. Lines no team owns stay contract-wide (e.g. the program management deliverables).",
                className: "mt-4",
                children: /*#__PURE__*/ _jsx(TeamLinesPicker, {
                    programId: programId,
                    base: base,
                    everyPeriod: everyPeriod,
                    onChange: (b, e)=>{
                        setBase(b);
                        setEveryPeriod(e);
                    },
                    testId: Testids.cmTeams.createLines
                })
            }),
            /*#__PURE__*/ _jsxs(SpecraFormField, {
                label: "SOW / Execution Plan",
                hint: SOW_DOCUMENT_CATEGORY_HELP,
                className: "mt-4",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "caption",
                        tone: "muted",
                        className: "mb-2",
                        "data-testid": Testids.cmTeams.createSowHint,
                        children: "Optional. Uploaded for this team after create; intake review appears on Modifications."
                    }),
                    /*#__PURE__*/ _jsx(FileUploader, {
                        files: sowFiles,
                        onChange: (f)=>setSowFiles(f.slice(-1)),
                        disabled: busy,
                        inputTestid: Testids.cmTeams.createSowUpload
                    })
                ]
            })
        ]
    });
}
